// Thread de comentários reutilizável (root + respostas até 2 níveis).
// Suporta menções via @email que disparam notificação in-app pro destinatário.
import { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Button, Tag, Typography, Space, Flex, App as AntApp, Mentions, Avatar, Divider, Spin,
} from 'antd';
import { API_URL } from '../../config';
import { getToken } from '../../utils/auth';
import { extrairMencoes } from './utils';

const { Text } = Typography;

// Renderiza texto destacando @emails como links visuais (não clicáveis por
// enquanto — só visual). Quebra em pedaços e re-monta com <strong> azul nos
// matches.
function renderTextoComMencoes(texto: string) {
  const re = /@([a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})/g;
  const partes: any[] = [];
  let last = 0;
  let m;
  let idx = 0;
  while ((m = re.exec(texto)) !== null) {
    if (m.index > last) partes.push(texto.slice(last, m.index));
    partes.push(<strong key={`m${idx++}`} style={{ color: '#0930D0' }}>@{m[1]}</strong>);
    last = m.index + m[0].length;
  }
  if (last < texto.length) partes.push(texto.slice(last));
  return partes.length === 0 ? texto : partes;
}

interface Props {
  atividadeId: string;
  meuEmail: string;
  usuarios: { email: string; nome: string }[];
}

export default function ComentariosThread({ atividadeId, meuEmail, usuarios }: Props) {
  const { message } = AntApp.useApp();
  const [comentarios, setComentarios] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [texto, setTexto] = useState('');
  const [respondendoA, setRespondendoA] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);

  async function carregar() {
    setLoading(true);
    try {
      const token = await getToken();
      const r = await axios.post(`${API_URL}/atividades`,
        { action: 'comentario_listar', atividade_id: atividadeId },
        { headers: { Authorization: token } });
      const d = r.data.body ? JSON.parse(r.data.body) : r.data;
      setComentarios(Array.isArray(d) ? d : []);
    } catch { /* silencioso */ }
    finally { setLoading(false); }
  }
  useEffect(() => { carregar(); /* eslint-disable-next-line */ }, [atividadeId]);

  async function enviar() {
    const t = texto.trim();
    if (!t) return;
    setEnviando(true);
    try {
      const token = await getToken();
      await axios.post(`${API_URL}/atividades`,
        { action: 'comentario_criar',
          atividade_id: atividadeId,
          parent_id: respondendoA,
          texto: t,
          mencoes: extrairMencoes(t) },
        { headers: { Authorization: token } });
      setTexto(''); setRespondendoA(null);
      await carregar();
    } catch (e: any) {
      message.error('Erro: ' + (e.response?.data?.erro || e.message));
    } finally { setEnviando(false); }
  }

  async function excluir(id: string) {
    if (!window.confirm('Excluir comentário?')) return;
    try {
      const token = await getToken();
      await axios.post(`${API_URL}/atividades`,
        { action: 'comentario_excluir', id },
        { headers: { Authorization: token } });
      await carregar();
    } catch (e: any) {
      message.error('Erro: ' + (e.response?.data?.erro || e.message));
    }
  }

  // Constrói árvore: comentários raiz + respostas indexadas por parent_id
  const raiz = comentarios.filter(c => !c.parent_id);
  const respostasDe = (id: string) => comentarios.filter(c => c.parent_id === id);

  // Opções do <Mentions>: lista de usuários ativos
  const mentionOptions = usuarios.map(u => ({
    value: u.email,
    label: `${u.nome} (${u.email})`,
  }));

  function renderComentario(c: any, profundidade = 0) {
    const respostas = respostasDe(c.id);
    const ehMeu = c.autor_email.toLowerCase() === meuEmail.toLowerCase();
    const iniciais = (c.autor_nome || c.autor_email).split(/[\s@]/)
      .filter(Boolean).slice(0, 2).map((s: string) => s[0]).join('').toUpperCase();
    return (
      <div key={c.id} style={{ marginLeft: profundidade * 24, marginTop: 8 }}>
        <Flex gap={8} align="flex-start">
          <Avatar size={28} style={{ background: '#020D7F', flexShrink: 0, fontSize: 11 }}>
            {iniciais}
          </Avatar>
          <div style={{ flex: 1, minWidth: 0 }}>
            <Flex gap={6} align="center" wrap>
              <Text strong style={{ fontSize: 12, color: '#020D7F' }}>
                {c.autor_nome || c.autor_email}
              </Text>
              <Text type="secondary" style={{ fontSize: 10 }}>
                {new Date(c.criado_em).toLocaleString('pt-BR')}
                {c.editado_em && ' · editado'}
              </Text>
            </Flex>
            <div style={{ fontSize: 12, color: '#222', whiteSpace: 'pre-wrap', marginTop: 2 }}>
              {renderTextoComMencoes(c.texto)}
            </div>
            {Array.isArray(c.mencoes) && c.mencoes.length > 0 && (
              <Space size={4} wrap style={{ marginTop: 3 }}>
                {c.mencoes.map((e: string) => (
                  <Tag key={e} color="blue" style={{ fontSize: 9, padding: '0 5px', lineHeight: '16px' }}>
                    🔔 {e}
                  </Tag>
                ))}
              </Space>
            )}
            <Space size={8} style={{ marginTop: 3 }}>
              {profundidade < 2 && (
                <Button size="small" type="link" style={{ fontSize: 10, padding: 0, height: 'auto' }}
                  onClick={() => { setRespondendoA(c.id); }}>
                  ↩ Responder
                </Button>
              )}
              {ehMeu && (
                <Button size="small" type="link" danger style={{ fontSize: 10, padding: 0, height: 'auto' }}
                  onClick={() => excluir(c.id)}>
                  Excluir
                </Button>
              )}
            </Space>
          </div>
        </Flex>
        {respostas.map(r => renderComentario(r, profundidade + 1))}
      </div>
    );
  }

  return (
    <div style={{ marginTop: 14 }}>
      <Divider style={{ margin: '12px 0 8px' }}>
        <Text type="secondary" style={{ fontSize: 11 }}>
          💬 Comentários {comentarios.length > 0 && `(${comentarios.length})`}
        </Text>
      </Divider>

      {loading ? <Spin size="small" /> : raiz.length === 0 ? (
        <Text type="secondary" italic style={{ fontSize: 11, display: 'block', textAlign: 'center', padding: 8 }}>
          Nenhum comentário ainda. Use @nome para mencionar alguém.
        </Text>
      ) : (
        <div style={{ maxHeight: 280, overflowY: 'auto', paddingRight: 6 }}>
          {raiz.map(c => renderComentario(c))}
        </div>
      )}

      <div style={{ marginTop: 10, background: '#FAFBFF', padding: 8, borderRadius: 5 }}>
        {respondendoA && (
          <Flex justify="space-between" align="center" style={{ marginBottom: 6 }}>
            <Text style={{ fontSize: 10, color: '#0930D0' }}>
              ↩ Respondendo a um comentário
            </Text>
            <Button size="small" type="link" style={{ fontSize: 10, padding: 0, height: 'auto' }}
              onClick={() => setRespondendoA(null)}>cancelar</Button>
          </Flex>
        )}
        <Mentions
          value={texto}
          onChange={setTexto}
          rows={2}
          placeholder="Comente aqui. Use @ para mencionar alguém."
          options={mentionOptions}
          autoSize={{ minRows: 2, maxRows: 4 }}
        />
        <Flex justify="flex-end" style={{ marginTop: 6 }}>
          <Button type="primary" size="small" loading={enviando}
            disabled={!texto.trim()} onClick={enviar}>
            {respondendoA ? 'Responder' : 'Comentar'}
          </Button>
        </Flex>
      </div>
    </div>
  );
}
